/**
 * Per-school module control — the module catalogue (v2).
 *
 * ONE definition of "which modules exist, what they are called, how the
 * Control Center groups them, which web routes and API route prefixes each
 * one owns, and what it depends on". It is used by:
 *   - the API gate (`apps/api/src/plugins/module-entitlements.ts`) to map a
 *     request URL to a module key;
 *   - the entitlement resolver + Control Center endpoints (dependencies,
 *     groups, core list);
 *   - the web nav / route guard / dashboards, to hide what a school switched off.
 *
 * **The API copy is GENERATED.** The API cannot import runtime values from
 * `@campushubai/shared` in production (the package entry re-exports
 * `export enum UserRole`, which compiled Node ESM cannot execute), so
 * `apps/api/src/lib/module-catalogue.ts` is a verbatim copy of THIS file.
 * Never edit it by hand: change this file, then run
 * `npm --prefix apps/api run sync:module-catalogue`. A test fails if the two
 * differ. Keep this file dependency-free (no imports) so that stays possible.
 *
 * Model (owner decisions, 2026-09-30):
 *   - Every school has EVERY module on by default. The Control Center turns
 *     modules off per school (and, later, per plan).
 *   - Nothing academic or financial is "core". The only things that can never
 *     be switched off are structural (`CORE_MODULES` below: sign-in, /me, the
 *     dashboard shell, the student/staff/class roster, school settings and
 *     members/roles, the notifications inbox, uploads, search, audit, health
 *     and internal tooling). They are not modules: they have no toggle and
 *     the Control Center PUT refuses to touch them.
 *   - A route belongs to exactly one owner (module or core) chosen by the
 *     LONGEST matching prefix over up to three path segments, `:param`
 *     segments being wildcards. `apiRouteCoverage` in the API test suite
 *     enumerates every registered route and fails if one has no owner, so a
 *     new route cannot silently bypass the gate.
 *
 * Money rule: fee rows already billed under Transport or Hostel are fee rows.
 * They are read, paid and collected through `fees` / `payments` endpoints,
 * which belong to the `fees` module, never to `transport` or `hostel`.
 * Switching Transport or Hostel off therefore never blocks a parent from
 * paying, nor the office from collecting.
 */

export const PLAN_VALUES = ["STARTER", "GROWTH", "ENTERPRISE"] as const;
export type Plan = (typeof PLAN_VALUES)[number];

export function isPlan(value: unknown): value is Plan {
  return typeof value === "string" && (PLAN_VALUES as readonly string[]).includes(value);
}

export type ModuleGroup =
  | "academics"
  | "finance"
  | "people"
  | "communication"
  | "operations"
  | "wellbeing"
  | "ai-learning"
  | "platform";

export const MODULE_GROUPS: { key: ModuleGroup; label: string }[] = [
  { key: "academics", label: "Academics" },
  { key: "finance", label: "Finance" },
  { key: "people", label: "People & HR" },
  { key: "communication", label: "Communication" },
  { key: "operations", label: "Operations" },
  { key: "wellbeing", label: "Wellbeing & compliance" },
  { key: "ai-learning", label: "AI & learning" },
  { key: "platform", label: "Platform" },
];

export type ModuleSurface = "web" | "app";

export interface ModuleCatalogueEntry {
  /** Stable key, also the value stored in `SchoolEntitlement.moduleKey`. */
  key: string;
  /** Human label for the Control Center and the "upgrade" teaser. */
  label: string;
  /** Control Center grouping. */
  group: ModuleGroup;
  /** Icon hint (lucide-style name); the UI maps it to a design-system icon. */
  icon: string;
  /**
   * Web route prefixes this module owns (a nav item at `/library` or
   * `/library/loans` both belong to `library`). Longest prefix wins, so a
   * deeper entry in another module (`/finance/accounts`) beats a shorter one
   * (`/finance`).
   */
  webPrefixes: string[];
  /**
   * API route prefixes relative to `/api/v1`, no leading/trailing slash, one
   * to three segments. `:` matches any single param segment
   * (`students/:/my-day`). A trailing `$` means exact match only (`health$`).
   * Longest prefix wins.
   */
  apiPrefixes: string[];
  /**
   * Keys that must be ON for this module to be on. Switching a module off
   * switches off everything that requires it (transitively); switching one on
   * needs its requirements on. Enforced by the Control Center PUT (dry-run
   * preview, `cascade:true` to apply).
   */
  requires: string[];
  /** Roles that use the module (UI hint for the Control Center / dashboards). */
  roles: string[];
  /** Where the module has a navigation entry. The API is always gated. */
  surfaces: ModuleSurface[];
}

/** Roles that see a disabled module as a locked "Upgrade" teaser instead of hiding it. */
export const MODULE_TEASER_ROLES: readonly string[] = ["ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"];

/**
 * Structural pieces that are NOT modules and can never be switched off. Each
 * lists the API prefixes it owns (same syntax as `ModuleCatalogueEntry.apiPrefixes`).
 * The Control Center PUT refuses these keys with `MODULE_IS_CORE`.
 */
export interface CoreModuleEntry {
  key: string;
  label: string;
  apiPrefixes: string[];
}

export const CORE_MODULES: CoreModuleEntry[] = [
  { key: "auth", label: "Sign-in & session", apiPrefixes: ["auth"] },
  { key: "me", label: "My account & profile", apiPrefixes: ["me", "users"] },
  {
    key: "dashboard",
    label: "Dashboard shell",
    apiPrefixes: ["dashboard", "daily-brief", "dashboard-config", "ai/dashboard-agent", "ai/dashboard-insights"],
  },
  {
    key: "roster",
    label: "Students, staff & classes",
    apiPrefixes: ["students", "student", "staff", "classes", "subjects", "buildings", "class-section-locations"],
  },
  {
    key: "settings",
    label: "School settings, members & roles",
    apiPrefixes: ["settings", "schools/me", "schools/:/managers", "invitations"],
  },
  { key: "notifications", label: "Notifications inbox", apiPrefixes: ["notifications", "push"] },
  { key: "uploads", label: "File uploads", apiPrefixes: ["upload"] },
  // The customer-facing Support desk (routes/support): "tell us what is wrong,
  // attach a screenshot, read our reply". A school must always be able to reach
  // us, so it is structural and has no toggle. (The internal QA board is the
  // separate, switchable `tickets` module below, platform team only.)
  { key: "support", label: "Support requests", apiPrefixes: ["support"] },
  { key: "search", label: "Search", apiPrefixes: ["search"] },
  { key: "audit", label: "Audit log", apiPrefixes: ["audit"] },
  { key: "onboarding", label: "Onboarding", apiPrefixes: ["onboarding"] },
  {
    key: "platform",
    label: "Platform plumbing (health, public verify links, webhooks, internal tooling)",
    apiPrefixes: [
      "health$",
      "health/face",
      "ping",
      "ready",
      "app",
      "verify",
      "webhooks",
      "control-center",
      "marketing",
      "client-onboarding",
      "qa",
      "e2e-runs",
      "testing-plan",
    ],
  },
];

/** Keys of the structural core items (they are not modules; the PUT refuses them). */
export const CORE_MODULE_KEYS: readonly string[] = CORE_MODULES.map((c) => c.key);

// ── role sets used by the catalogue below ───────────────────────────────────
const ADM = ["CHAIRMAN", "DIRECTOR", "ADMIN", "PRINCIPAL"];
const ADM_TEACH = [...ADM, "TEACHER"];
const ADM_STAFF = [...ADM, "TEACHER", "STAFF"];
const ADM_FAMILY = [...ADM, "TEACHER", "STUDENT", "PARENT"];
const EVERYONE = [...ADM, "TEACHER", "STAFF", "STUDENT", "PARENT"];
const WEB_APP: ModuleSurface[] = ["web", "app"];
const WEB_ONLY: ModuleSurface[] = ["web"];

function mod(
  key: string,
  label: string,
  group: ModuleGroup,
  icon: string,
  o: {
    web?: string[];
    api?: string[];
    requires?: string[];
    roles?: string[];
    surfaces?: ModuleSurface[];
  },
): ModuleCatalogueEntry {
  return {
    key,
    label,
    group,
    icon,
    webPrefixes: o.web ?? [],
    apiPrefixes: o.api ?? [],
    requires: o.requires ?? [],
    roles: o.roles ?? ADM,
    surfaces: o.surfaces ?? WEB_APP,
  };
}

export const MODULE_CATALOGUE: ModuleCatalogueEntry[] = [
  // ── Academics ─────────────────────────────────────────────────────────────
  mod("exams", "Examinations & report cards", "academics", "clipboard-check", {
    web: ["/exams", "/hall-tickets", "/gradebook"],
    api: ["exams", "exam-cycles", "hall-tickets", "pdf/report-card", "pdf/report-cards", "ai/report-card", "dashboard/chairman/exams"],
    roles: EVERYONE,
  }),
  mod("timetable", "Timetable", "academics", "calendar-clock", {
    web: ["/timetable"],
    api: ["timetable"],
    roles: EVERYONE,
  }),
  mod("substitutes", "Substitute teachers", "academics", "user-cog", {
    web: ["/substitutes"],
    api: ["substitutes"],
    requires: ["timetable"],
    roles: ADM_TEACH,
  }),
  mod("homework", "Homework & diary", "academics", "notebook-pen", {
    web: ["/homework"],
    api: ["homework", "ai/homework", "students/:/diary-summary"],
    roles: ADM_FAMILY,
  }),
  mod("curriculum", "Curriculum & year plan", "academics", "book-open", {
    web: ["/curriculum"],
    api: ["curriculums", "curriculum-proposals", "dashboard/chairman/curriculum"],
    roles: ADM_TEACH,
  }),
  mod("lessons", "Teacher daily updates", "academics", "list-checks", {
    web: ["/lessons", "/teachers-updates"],
    api: ["lesson-logs"],
    requires: ["timetable", "curriculum"],
    roles: ADM_FAMILY,
  }),
  mod("competency", "Competency-based education", "academics", "target", {
    web: ["/competency"],
    api: ["competency"],
    roles: ADM_TEACH,
  }),
  mod("hpcard", "NEP holistic progress card", "academics", "id-card", {
    web: ["/hpcard"],
    api: ["hpcards"],
    requires: ["exams"],
    roles: ADM_FAMILY,
  }),
  mod("udise", "UDISE+ reporting", "academics", "file-bar-chart", {
    web: ["/udise"],
    api: ["udise"],
    surfaces: WEB_ONLY,
  }),
  mod("my-day", "My Day (pre-primary)", "academics", "sun", {
    web: ["/my-day"],
    api: ["child-experience", "students/:/my-day"],
    roles: ADM_FAMILY,
  }),
  mod("admissions", "Admissions", "academics", "user-plus", {
    web: ["/admissions"],
    api: ["admissions", "admission-tests", "pdf/admission", "dashboard/chairman/admissions"],
    requires: ["fees"],
    roles: ADM_STAFF,
  }),

  // ── Finance ───────────────────────────────────────────────────────────────
  mod("fees", "Fees & collections", "finance", "wallet", {
    web: ["/finance"],
    api: [
      "fees",
      "payments",
      "corrections",
      "pdf/fee-receipt",
      "settings/payments",
      "notifications/send-fee-reminder",
      "notifications/bulk-fee-reminder",
      "dashboard/chairman/finance",
      "dashboard/school-finance",
    ],
    roles: [...ADM, "STAFF", "PARENT"],
  }),
  mod("finance", "Expenses", "finance", "receipt", {
    api: ["expenses"],
  }),
  mod("accounting", "Accounting ledger", "finance", "book-text", {
    web: ["/accounting", "/finance/accounts"],
    api: ["gl"],
    requires: ["finance"],
    surfaces: WEB_ONLY,
  }),
  mod("budget", "Budget planning", "finance", "pie-chart", {
    web: ["/budget"],
    api: ["budget"],
    requires: ["finance"],
    surfaces: WEB_ONLY,
  }),
  mod("concessions", "Fee concessions", "finance", "badge-percent", {
    web: ["/concession-approvals"],
    api: ["concessions", "schools/me/concession-types"],
    requires: ["fees"],
  }),
  mod("scholarships", "Scholarships", "finance", "graduation-cap", {
    web: ["/scholarships"],
    api: ["scholarships", "dashboard/chairman/scholarships"],
    requires: ["fees"],
    roles: [...ADM, "STUDENT", "PARENT"],
  }),
  mod("donations", "Donations & Fundraising", "finance", "heart-handshake", {
    // `/give` is the public campaign/transparency page and `/verify/donation`
    // the public receipt check — both belong to the module so a school with
    // Donations off serves neither.
    web: ["/donations", "/give", "/verify/donation"],
    api: ["donations"],
    roles: EVERYONE,
  }),

  // ── People & HR ───────────────────────────────────────────────────────────
  mod("attendance", "Attendance", "people", "user-check", {
    web: ["/attendance"],
    api: [
      "attendance",
      "staff-attendance",
      "settings/attendance-sms",
      "settings/attendance-window",
      "settings/geofence",
      "settings/kiosk-account",
      "notifications/send-attendance-alert",
      "dashboard/chairman/attendance",
    ],
    roles: EVERYONE,
  }),
  mod("hr", "HR, payroll & performance", "people", "briefcase", {
    web: ["/hr"],
    api: ["hr", "performance", "pdf/salary-slip"],
    roles: [...ADM, "STAFF", "TEACHER"],
  }),
  mod("cpd", "Teacher CPD", "people", "award", {
    web: ["/cpd"],
    api: ["cpd"],
    roles: ADM_TEACH,
  }),
  mod("biometric", "Biometric & face attendance", "people", "fingerprint", {
    web: ["/biometric"],
    api: ["biometric", "face"],
    requires: ["attendance"],
    roles: ADM_STAFF,
    surfaces: WEB_ONLY,
  }),

  // ── Communication ─────────────────────────────────────────────────────────
  mod("announcements", "Announcements & notices", "communication", "megaphone", {
    web: ["/communication/announcements", "/communication/notices", "/website"],
    api: ["announcements", "school-posts", "school-website", "dashboard/chairman/announcements"],
    roles: EVERYONE,
  }),
  mod("messages", "Messages", "communication", "message-square", {
    web: ["/communication/messages"],
    api: ["messages", "dashboard/chairman/messages"],
    roles: EVERYONE,
  }),
  mod("communications", "Bulk SMS, WhatsApp & email", "communication", "send", {
    web: ["/communications", "/campaigns"],
    api: ["communications", "email", "sms", "whatsapp"],
    surfaces: WEB_ONLY,
  }),
  mod("calendar", "Calendar", "communication", "calendar", {
    web: ["/calendar"],
    api: ["calendar"],
    roles: EVERYONE,
  }),
  mod("meetings", "Video meetings & live classes", "communication", "video", {
    web: ["/meetings", "/classroom"],
    // meeting-guest: the public guest link (2026-10-11). The entitlement gate skips
    // signed-out requests, so routes/meetings/guests.ts checks the module itself.
    api: ["meetings", "classroom", "meeting-guest", "dashboard/chairman/meetings"],
    roles: EVERYONE,
  }),
  mod("ptm", "Parent-teacher meetings", "communication", "users", {
    web: ["/ptm"],
    api: ["ptm"],
    requires: ["meetings"],
    roles: ADM_FAMILY,
  }),
  mod("newsletter", "Newsletter", "communication", "newspaper", {
    web: ["/newsletter", "/studio/newsletter"],
    api: ["newsletter", "dashboard/chairman/newsletter"],
    roles: EVERYONE,
  }),
  mod("forums", "Forums", "communication", "messages-square", {
    web: ["/forums"],
    api: ["forums"],
    roles: EVERYONE,
  }),
  mod("surveys", "Surveys", "communication", "list-todo", {
    web: ["/surveys"],
    api: ["surveys"],
    roles: EVERYONE,
  }),
  mod("feedback", "Feedback", "communication", "message-circle-heart", {
    web: ["/feedback"],
    api: ["feedback", "dashboard/chairman/feedback"],
    roles: EVERYONE,
  }),

  // ── Operations ────────────────────────────────────────────────────────────
  mod("transport", "Transport", "operations", "bus", {
    web: ["/transport"],
    api: ["transport", "dashboard/chairman/transport"],
    roles: [...ADM, "STAFF", "DRIVER", "PARENT", "STUDENT"],
  }),
  mod("drive", "Driver app", "operations", "steering-wheel", {
    web: ["/drive"],
    requires: ["transport"],
    roles: ["DRIVER", ...ADM],
  }),
  mod("hostel", "Hostel", "operations", "bed", {
    web: ["/hostel"],
    api: ["hostel"],
    roles: [...ADM, "STAFF", "PARENT", "STUDENT"],
  }),
  mod("library", "Library", "operations", "library", {
    web: ["/library"],
    api: ["library"],
    roles: EVERYONE,
  }),
  mod("canteen", "Canteen", "operations", "utensils", {
    web: ["/canteen"],
    api: ["canteen"],
    roles: EVERYONE,
  }),
  mod("health", "Health & safety", "operations", "heart-pulse", {
    web: ["/health"],
    api: ["health"],
    roles: ADM_STAFF,
  }),
  mod("passes", "Gate & visitor passes", "operations", "ticket", {
    web: ["/gate-passes", "/visitor-passes"],
    api: ["passes"],
    roles: ADM_STAFF,
  }),
  mod("certificates", "Certificates", "operations", "scroll-text", {
    web: ["/certificates", "/studio/certificates"],
    api: ["certificates", "documents/certificates", "pdf/certificate"],
    roles: ADM_FAMILY,
  }),
  mod("id-cards", "ID cards", "operations", "contact", {
    web: ["/id-cards"],
    api: ["documents/id-cards"],
  }),
  mod("posters", "Posters & share cards", "operations", "image", {
    web: ["/posters"],
    api: ["documents/posters"],
  }),
  mod("event-tickets", "Event tickets", "operations", "ticket-check", {
    web: ["/event-tickets"],
    api: ["documents/tickets"],
    roles: ADM_STAFF,
  }),
  mod("recognition", "Recognition & share cards", "operations", "medal", {
    web: ["/recognition"],
    api: ["recognition", "documents/share-consent"],
    roles: EVERYONE,
  }),

  // ── Wellbeing & compliance ────────────────────────────────────────────────
  mod("counselling", "Counselling", "wellbeing", "heart", {
    web: ["/counselling"],
    api: ["counselling"],
    roles: [...ADM, "TEACHER", "STUDENT", "PARENT"],
  }),
  mod("sel", "Social-emotional learning", "wellbeing", "smile", {
    web: ["/sel"],
    api: ["sel"],
    roles: ADM_FAMILY,
  }),
  mod("special-education", "Special education (IEP)", "wellbeing", "accessibility", {
    web: ["/special-education"],
    api: ["iep"],
    roles: ADM_TEACH,
  }),
  mod("dpdp", "DPDP compliance", "wellbeing", "shield-check", {
    web: ["/dpdp"],
    api: ["dpdp", "dashboard/chairman/dpdp"],
    roles: [...ADM, "PARENT"],
  }),
  mod("compliance", "Compliance register", "wellbeing", "clipboard-list", {
    web: ["/compliance"],
    api: ["compliance", "dashboard/chairman/compliance"],
    surfaces: WEB_ONLY,
  }),
  mod("early-warning", "AI early warning", "wellbeing", "siren", {
    web: ["/early-warning"],
    api: ["early-warning", "dashboard/chairman/early-warning"],
    roles: ADM_TEACH,
  }),

  // ── AI & learning ─────────────────────────────────────────────────────────
  mod("hubba", "Hubba AI assistant", "ai-learning", "sparkles", {
    web: ["/hubba"],
    api: ["hubba", "rag"],
    roles: EVERYONE,
  }),
  mod("ai-tutor", "AI advisor & study plans", "ai-learning", "brain", {
    web: ["/ai-tutor"],
    api: ["ai/tutor", "ai/study-plan", "ai/sessions"],
    roles: ADM_FAMILY,
  }),
  mod("digital-learner", "Digital learner", "ai-learning", "laptop", {
    web: ["/digital-learner", "/tutor"],
    api: ["digital-learner", "ai-teacher"],
    roles: ADM_FAMILY,
  }),
  mod("learning", "AI learning OS", "ai-learning", "graduation-cap", {
    web: ["/learning"],
    api: ["learning"],
    roles: ADM_FAMILY,
  }),
  mod("adaptive-learning", "Adaptive learning", "ai-learning", "route", {
    web: ["/adaptive-learning"],
    api: ["adaptive"],
    roles: ADM_FAMILY,
  }),
  mod("scorm", "SCORM content library", "ai-learning", "package", {
    web: ["/scorm"],
    api: ["scorm"],
    roles: ADM_FAMILY,
  }),
  mod("playground", "Brain arcade", "ai-learning", "gamepad-2", {
    web: ["/playground"],
    api: [
      "games",
      "game-sessions",
      "game-assignments",
      "game-content-packs",
      "game-analytics",
      "students/:/game-progress",
    ],
    roles: ADM_FAMILY,
  }),
  mod("plagiarism", "Plagiarism check", "ai-learning", "scan-search", {
    web: ["/plagiarism"],
    api: ["plagiarism"],
    roles: ADM_TEACH,
  }),
  mod("faircopy", "Faircopy", "ai-learning", "pen-line", {
    web: ["/faircopy"],
    api: ["faircopy"],
    roles: ADM_FAMILY,
  }),
  mod("science-lab", "Science lab", "ai-learning", "flask-conical", {
    web: ["/science-lab"],
    roles: ADM_FAMILY,
  }),
  mod("career", "Career guidance", "ai-learning", "compass", {
    web: ["/career"],
    api: ["ai/career"],
    roles: ADM_FAMILY,
  }),
  mod("badges", "Badges", "ai-learning", "badge-check", {
    web: ["/badges"],
    api: ["badges", "me/badges"],
    roles: EVERYONE,
  }),

  // ── Platform ──────────────────────────────────────────────────────────────
  mod("analytics", "Analytics", "platform", "bar-chart-3", {
    web: ["/analytics"],
    api: ["analytics", "dashboard/chairman/analytics"],
    surfaces: WEB_ONLY,
  }),
  mod("automation", "Automation rules", "platform", "workflow", {
    web: ["/automation"],
    api: ["automation"],
    surfaces: WEB_ONLY,
  }),
  mod("dashboard-builder", "Dashboard builder", "platform", "layout-dashboard", {
    web: ["/settings/dashboard-builder"],
    // No API prefix on purpose: GET /dashboard-config/:role is read by every
    // dashboard (core). Turning this off hides the builder page only.
    surfaces: WEB_ONLY,
  }),
  mod("studio", "Studio", "platform", "palette", {
    web: ["/studio"],
    api: ["studio"],
    surfaces: WEB_ONLY,
  }),
  mod("tickets", "Support tickets", "platform", "life-buoy", {
    web: ["/tickets"],
    api: ["tickets"],
    roles: EVERYONE,
  }),
  mod("alumni", "Alumni network", "platform", "graduation-cap", {
    web: ["/alumni"],
    api: ["alumni"],
  }),
  mod("integrations", "Integrations marketplace", "platform", "plug", {
    web: ["/integrations", "/settings/integrations"],
    api: ["integrations"],
    surfaces: WEB_ONLY,
  }),
  mod("schools", "Multi-school network", "platform", "building-2", {
    web: ["/schools", "/school-health"],
    api: ["schools"],
    roles: ["CHAIRMAN", "DIRECTOR"],
  }),
];

// ── Pure helpers ────────────────────────────────────────────────────────────

/** Every module key the catalogue knows about. */
export function allModuleKeys(): string[] {
  return MODULE_CATALOGUE.map((m) => m.key);
}

export function moduleByKey(key: string): ModuleCatalogueEntry | undefined {
  return MODULE_CATALOGUE.find((m) => m.key === key);
}

export type ApiRouteOwner = { kind: "module"; key: string } | { kind: "core"; key: string };

/**
 * Normalise a path into comparable segments: drop the query string and
 * `/api/v1`, drop empty segments, lower-case, and turn Fastify `:param`
 * segments into the wildcard `:`.
 */
function segmentsOf(path: string, stripApiV1: boolean): string[] {
  let p = path.split("?")[0] ?? "";
  if (stripApiV1 && p.startsWith("/api/v1")) p = p.slice("/api/v1".length);
  return p
    .split("/")
    .filter((s) => s.length > 0)
    .map((s) => (s.startsWith(":") ? ":" : s.toLowerCase()));
}

const MAX_PREFIX_SEGMENTS = 3;

function buildOwnerIndex(
  entries: { key: string; prefixes: string[]; kind: "module" | "core" }[],
  normalise: (prefix: string) => { keyStr: string; exact: boolean },
): { prefix: Map<string, ApiRouteOwner>; exact: Map<string, ApiRouteOwner> } {
  const prefix = new Map<string, ApiRouteOwner>();
  const exact = new Map<string, ApiRouteOwner>();
  for (const e of entries) {
    for (const raw of e.prefixes) {
      const { keyStr, exact: isExact } = normalise(raw);
      (isExact ? exact : prefix).set(keyStr, { kind: e.kind, key: e.key });
    }
  }
  return { prefix, exact };
}

const apiIndex = buildOwnerIndex(
  [
    ...MODULE_CATALOGUE.map((m) => ({ key: m.key, prefixes: m.apiPrefixes, kind: "module" as const })),
    ...CORE_MODULES.map((c) => ({ key: c.key, prefixes: c.apiPrefixes, kind: "core" as const })),
  ],
  (raw) => {
    const exact = raw.endsWith("$");
    const body = exact ? raw.slice(0, -1) : raw;
    return { keyStr: body.replace(/^\/+|\/+$/g, ""), exact };
  },
);

/**
 * Who owns an API path (relative to `/api/v1`, or with the `/api/v1` prefix)?
 * Longest prefix over up to three segments wins; an exact (`$`) entry wins over
 * a prefix of the same length. Accepts both a Fastify route template
 * (`/students/:id/my-day`) and a concrete URL (`/students/abc/my-day`): a
 * concrete second segment is retried as the `:` wildcard.
 */
export function resolveApiPath(pathAfterV1: string): ApiRouteOwner | null {
  const segs = segmentsOf(pathAfterV1, true);
  if (segs.length === 0) return null;
  const n = Math.min(segs.length, MAX_PREFIX_SEGMENTS);
  for (let len = n; len >= 1; len--) {
    const head = segs.slice(0, len);
    const variants = [head.join("/")];
    if (len >= 2 && head[1] !== ":") variants.push([head[0], ":", ...head.slice(2)].join("/"));
    if (len === segs.length) {
      for (const v of variants) {
        const hit = apiIndex.exact.get(v);
        if (hit) return hit;
      }
    }
    for (const v of variants) {
      const hit = apiIndex.prefix.get(v);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Resolve an `/api/v1`-relative path to a catalogue MODULE key, or `null` if
 * it belongs to core or to nothing. Longest prefix wins.
 */
export function moduleKeyForApiPath(pathAfterV1: string): string | null {
  const owner = resolveApiPath(pathAfterV1);
  return owner && owner.kind === "module" ? owner.key : null;
}

const webIndex = (() => {
  const m = new Map<string, string>();
  for (const entry of MODULE_CATALOGUE) {
    for (const p of entry.webPrefixes) m.set(p.replace(/\/+$/, "").toLowerCase(), entry.key);
  }
  return m;
})();

/** Resolve a web pathname to a catalogue module key (longest prefix wins), or `null`. */
export function moduleKeyForWebPath(pathname: string): string | null {
  const segs = (pathname.split("?")[0] ?? "").split("/").filter((s) => s.length > 0).map((s) => s.toLowerCase());
  for (let len = Math.min(segs.length, 4); len >= 1; len--) {
    const hit = webIndex.get(`/${segs.slice(0, len).join("/")}`);
    if (hit) return hit;
  }
  return null;
}

/** Keys that directly require `key`. */
export function directDependantsOf(key: string): string[] {
  return MODULE_CATALOGUE.filter((m) => m.requires.includes(key)).map((m) => m.key);
}

/** Every module that (transitively) requires `key`, nearest first, not including `key`. */
export function dependantsOf(key: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>([key]);
  let frontier = [key];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const k of frontier) {
      for (const d of directDependantsOf(k)) {
        if (!seen.has(d)) {
          seen.add(d);
          out.push(d);
          next.push(d);
        }
      }
    }
    frontier = next;
  }
  return out;
}

/** Every module `key` (transitively) requires, not including `key`. */
export function requirementsOf(key: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>([key]);
  let frontier = [key];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const k of frontier) {
      for (const r of moduleByKey(k)?.requires ?? []) {
        if (!seen.has(r)) {
          seen.add(r);
          out.push(r);
          next.push(r);
        }
      }
    }
    frontier = next;
  }
  return out;
}
